import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { DEVICE_SIZE, mq } from '@marty-js/design/src/utils/mq';
import { ButtonDropdown } from '@marty-js/design/src/molecules/download/button-dropdown';
import dynamic from 'next/dynamic';
import { EditoGrid } from '../../../../article-processor/edito-grid';
import { SlateContentPlugin } from '../../../../article-processor/plugins/slate.content';
import type { DownloadItemProps } from './types';
import { useDatalayer } from '../../../../utils/AnalyticsContext';
import { LoadLegacyCss } from '../../../../atoms/loadLegacyCss';
import { useAdContext, useAddServerSideZones } from '../../../../ads/AdContext';
import { DataType, SponsoFactory, TemplateType } from '../../../../utils/sponsoFactory';
import { DataZone } from '../../../../ads/types';
import { ComponentProps } from '../../../../types';

const Caret = dynamic(() => import('@marty-js/design/src/icons/caret'));

const LeftSide = styled.div`
  position: relative;
`;

const SoftwareHeader = styled.div`
  display: flex;
  flex-direction: column-reverse;
  margin: 0 0 15px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      flex-direction: column;
      margin: 0 0 45px;
    `,
  )}
`;

const DownloadRatingConclusion = styled.div`
  display: block;
  margin: 30px 0 0;

  p {
    font-weight: bold;
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      margin: 15px 60px 30px;

      p {
        font-size: 20px;
        line-height: 32px;
      }
    `,
  )}
`;

type Props = DownloadItemProps & {
  legacy: boolean;
  // os: string;
  // handlerStartDownload: (id: string, os: string) => void;
  campaignName: string;
  links: any;
  setIsModal: any;
  userAgentOs: string;
};

const DownloadSheet = ({ legacy, item, data, file, campaignName, links, setIsModal, userAgentOs }: Props) => {
  const adContext = useAdContext();

  const zoneSponsoLink = useMemo(() => ({ zoneSlug: 'sponso-dl-head-link', keywords: '' }), []);
  const dataSponsoLink = useAddServerSideZones(zoneSponsoLink);

  const variant = item.variants?.find((curVariant) => curVariant.id === file.variantId);

  const brand = useMemo<string>(() => {
    const softwareSpec = variant?.dataSheet?.groups.find((group) => group.shortSlug === 'specs');
    const softwareBrand = softwareSpec?.attributes.find((attribute) => attribute.shortSlug === 'software-publisher');

    return softwareBrand?.value ?? '';
  }, [variant]);

  useDatalayer({ brandTag: brand ?? '', campaign: campaignName });

  const Content = useMemo(
    () =>
      legacy ? (
        <div className="article-content" dangerouslySetInnerHTML={{ __html: data.legacyArticle?.data.html }} />
      ) : (
        <EditoGrid
          articleJson={data.mainArticle.articleJson}
          links={data.mainArticle.index}
          firstTitleRow={data.mainArticle.firstTitleRow}
        />
      ),
    [legacy, data.mainArticle, data.legacyArticle],
  );

  const isModalCallBack = useCallback(() => {
    return userAgentOs !== 'iOS' && userAgentOs !== 'Android' ? setIsModal(adContext.isModal) : null;
  }, [setIsModal, userAgentOs, adContext.isModal]);

  return (
    <>
      {legacy ? <LoadLegacyCss /> : null}

      <div onMouseEnter={isModalCallBack} id="download-button">
        <ButtonDropdown links={links} isRotate>
          {links.length > 1 && <Caret width={10} height={5} color="var(--theme-palette-white)" />}
        </ButtonDropdown>
      </div>

      {dataSponsoLink?.isEmpty ? null : (
        <SponsoFactory zone={dataSponsoLink as DataZone<ComponentProps<DataType>, TemplateType>} showMobile />
      )}

      <LeftSide />

      <SoftwareHeader>
        {item.ratingConclusion && (
          <DownloadRatingConclusion>
            <SlateContentPlugin cell={{ size: 12, content: { state: JSON.parse(item.ratingConclusion) } }} />
          </DownloadRatingConclusion>
        )}
      </SoftwareHeader>

      {Content}
    </>
  );
};

export default DownloadSheet;
